import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import FileAttachments from '../../components/FileAttachments';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Target, Plus, CheckCircle, Circle, XCircle, Clock,
  Users, FolderSimple, Link as LinkIcon, Trash, PencilSimple,
  CaretRight, Flag, Lightning, Warning, ArrowRight
} from '@phosphor-icons/react';

const PRIORITY_COLORS = { low: '#22c55e', medium: '#eab308', high: '#f97316', critical: '#ef4444' };
const TASK_STATUS = { todo: { label: 'To Do', color: '#94a3b8' }, in_progress: { label: 'In Progress', color: '#3b82f6' }, done: { label: 'Done', color: '#22c55e' }, blocked: { label: 'Blocked', color: '#ef4444' } };
const ENTITY_TYPES = ['experiment', 'protocol', 'plasmid', 'strain', 'sample'];

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showBenchmarkForm, setShowBenchmarkForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [editingStage, setEditingStage] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', phase: '', subfolder: '', due_date: '', assigned_to_name: '' });
  const [bmForm, setBmForm] = useState({ title: '', type: 'milestone', target_value: '', target_date: '', criteria: '', phase: '', description: '' });
  const [linkForm, setLinkForm] = useState({ entity_type: 'experiment', entity_id: '', entity_name: '' });
  const [folderName, setFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);

  const load = useCallback(async () => {
    try {
      const p = await api.getProject(id);
      setProject(p);
    } catch (e) { toast.error('Failed to load project'); navigate('/projects'); }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const advanceStage = async (stageKey) => {
    await api.updateProject(id, { current_stage: stageKey });
    toast.success(`Moved to ${stageKey}`);
    load();
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;
    await api.createTask(id, { ...taskForm, subfolder: selectedFolder || taskForm.subfolder || null });
    toast.success('Task added');
    setShowTaskForm(false);
    setTaskForm({ title: '', description: '', priority: 'medium', phase: '', subfolder: '', due_date: '', assigned_to_name: '' });
    load();
  };

  const toggleTask = async (taskId, currentStatus) => {
    const next = currentStatus === 'done' ? 'todo' : currentStatus === 'todo' ? 'in_progress' : 'done';
    await api.updateTask(id, taskId, { status: next });
    load();
  };

  const deleteTask = async (taskId) => {
    await api.deleteTask(id, taskId);
    load();
  };

  const addBenchmark = async (e) => {
    e.preventDefault();
    if (!bmForm.title.trim()) return;
    await api.addBenchmark(id, bmForm);
    toast.success('Benchmark added');
    setShowBenchmarkForm(false);
    setBmForm({ title: '', type: 'milestone', target_value: '', target_date: '', criteria: '', phase: '', description: '' });
    load();
  };

  const toggleBenchmark = async (bmId, currentStatus) => {
    const next = currentStatus === 'met' ? 'pending' : 'met';
    await api.updateBenchmark(id, bmId, { status: next });
    toast.success(next === 'met' ? 'Benchmark achieved!' : 'Benchmark reopened');
    load();
  };

  const linkEntity = async (e) => {
    e.preventDefault();
    if (!linkForm.entity_name.trim()) return;
    await api.linkEntity(id, linkForm);
    toast.success('Entity linked');
    setShowLinkForm(false);
    setLinkForm({ entity_type: 'experiment', entity_id: '', entity_name: '' });
    load();
  };

  const addFolder = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    await api.addSubfolder(id, { name: folderName, parent_id: selectedFolder });
    toast.success('Folder created');
    setShowFolderForm(false);
    setFolderName('');
    load();
  };

  if (loading || !project) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const stages = project.pipeline_stages || [];
  const currentIdx = stages.findIndex(s => s.key === project.current_stage);
  const tasks = project.tasks || [];
  const benchmarks = project.benchmarks || [];
  const folders = project.subfolders || [];
  const links = project.linked_entities || [];
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const metBenchmarks = benchmarks.filter(b => b.status === 'met').length;

  // Get tasks for current folder view
  const visibleTasks = selectedFolder
    ? tasks.filter(t => t.subfolder === selectedFolder)
    : tasks.filter(t => !t.subfolder);

  return (
    <div data-testid="project-detail">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <button onClick={() => navigate('/projects')} className="mt-1 text-slate-400 hover:text-slate-300"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="heading-display">{project.title}</h1>
            <select
              className="text-xs px-2 py-1 rounded-full font-medium border"
              value={project.status}
              onChange={async (e) => { await api.updateProject(id, { status: e.target.value }); load(); }}
              data-testid="project-status-select"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          {project.description && <p className="text-sm text-slate-400 mt-1">{project.description}</p>}
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
            {project.start_date && <span className="flex items-center gap-1"><Clock size={12} /> Started: {project.start_date}</span>}
            {project.target_end_date && <span className="flex items-center gap-1"><Flag size={12} /> Target: {project.target_end_date}</span>}
            <span className="flex items-center gap-1"><CheckCircle size={12} /> {doneTasks}/{totalTasks} tasks</span>
            <span className="flex items-center gap-1"><Target size={12} /> {metBenchmarks}/{benchmarks.length} benchmarks</span>
            {project.team?.length > 0 && <span className="flex items-center gap-1"><Users size={12} /> {project.team.map(t => t.name).join(', ')}</span>}
          </div>
        </div>
      </div>

      {/* Pipeline Visualization */}
      <div className="card-flat p-5 mb-6" data-testid="pipeline-view">
        <div className="flex items-center justify-between mb-3">
          <h3 className="heading-card">Pipeline</h3>
        </div>
        <div className="flex items-center gap-2">
          {stages.map((stage, i) => {
            const isCompleted = i < currentIdx;
            const isCurrent = i === currentIdx;
            return (
              <React.Fragment key={stage.key}>
                {i > 0 && <CaretRight size={14} className="text-slate-300 flex-shrink-0" />}
                <button
                  className={`flex-1 px-3 py-3 rounded-lg text-center transition-all border-2 ${
                    isCurrent ? 'border-current shadow-sm' : isCompleted ? 'border-transparent opacity-80' : 'border-transparent opacity-40'
                  }`}
                  style={{
                    backgroundColor: (isCompleted || isCurrent) ? `${stage.color}15` : '#f8fafc',
                    borderColor: isCurrent ? stage.color : 'transparent',
                    color: (isCompleted || isCurrent) ? stage.color : '#94a3b8'
                  }}
                  onClick={() => advanceStage(stage.key)}
                  data-testid={`stage-${stage.key}`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    {isCompleted ? <CheckCircle size={14} weight="fill" /> : isCurrent ? <Lightning size={14} weight="fill" /> : <Circle size={14} />}
                    <span className="text-xs font-semibold">{stage.label}</span>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-700 pb-2">
        {[
          { key: 'tasks', label: 'Tasks', count: totalTasks },
          { key: 'benchmarks', label: 'Benchmarks', count: benchmarks.length },
          { key: 'folders', label: 'Folders', count: folders.length },
          { key: 'linked', label: 'Linked Entities', count: links.length },
          { key: 'team', label: 'Team', count: project.team?.length || 0 },
        ].map(tab => (
          <button key={tab.key}
            className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === tab.key ? 'text-indigo-300 border-b-2 border-indigo-400' : 'text-slate-400 hover:text-slate-300'}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'tasks' && (
        <div data-testid="tasks-section">
          {/* Folder breadcrumb */}
          {selectedFolder && (
            <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
              <button onClick={() => setSelectedFolder(null)} className="hover:text-indigo-400">Root</button>
              <CaretRight size={10} />
              <span className="font-medium text-slate-200">{folders.find(f => f.id === selectedFolder)?.name || selectedFolder}</span>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {!selectedFolder && folders.map(f => (
                <button key={f.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 hover:border-indigo-400 text-xs text-slate-300 hover:text-indigo-300 transition-colors"
                  onClick={() => setSelectedFolder(f.id)}>
                  <FolderSimple size={14} /> {f.name}
                </button>
              ))}
            </div>
            <Button size="sm" className="btn-primary text-xs" onClick={() => setShowTaskForm(true)} data-testid="add-task-btn">
              <Plus size={14} className="mr-1" /> Add Task
            </Button>
          </div>
          {visibleTasks.length === 0 ? (
            <div className="card-flat p-8 text-center text-slate-400 text-sm">No tasks {selectedFolder ? 'in this folder' : 'yet'}. Add one to get started.</div>
          ) : (
            <div className="space-y-2">
              {visibleTasks.map(task => {
                const st = TASK_STATUS[task.status] || TASK_STATUS.todo;
                return (
                  <div key={task.id} className="card-flat p-3 flex items-center gap-3 hover:border-slate-700 transition-colors" data-testid={`task-${task.id}`}>
                    <button onClick={() => toggleTask(task.id, task.status)} className="flex-shrink-0" data-testid={`toggle-task-${task.id}`}>
                      {task.status === 'done' ? <CheckCircle size={20} weight="fill" className="text-green-500" /> :
                       task.status === 'in_progress' ? <Circle size={20} className="text-blue-500" /> :
                       task.status === 'blocked' ? <Warning size={20} className="text-red-500" /> :
                       <Circle size={20} className="text-slate-300" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-slate-400' : ''}`} style={{ color: task.status !== 'done' ? '#F1F5F9' : undefined }}>{task.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${st.color}20`, color: st.color }}>{st.label}</span>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[task.priority] }} title={task.priority} />
                        {task.phase && <span className="text-[10px] text-slate-400">{task.phase}</span>}
                        {task.assigned_to_name && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Users size={10} /> {task.assigned_to_name}</span>}
                        {task.due_date && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock size={10} /> {task.due_date}</span>}
                      </div>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash size={14} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'benchmarks' && (
        <div data-testid="benchmarks-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-400">{metBenchmarks} of {benchmarks.length} benchmarks met</p>
            <Button size="sm" className="btn-primary text-xs" onClick={() => setShowBenchmarkForm(true)} data-testid="add-benchmark-btn">
              <Plus size={14} className="mr-1" /> Add Benchmark
            </Button>
          </div>
          {/* Progress bar */}
          {benchmarks.length > 0 && (
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-indigo-900/300 rounded-full transition-all" style={{ width: `${benchmarks.length ? (metBenchmarks / benchmarks.length) * 100 : 0}%` }} />
            </div>
          )}
          <div className="space-y-2">
            {benchmarks.map(bm => (
              <div key={bm.id} className={`card-flat p-4 border-l-4 ${bm.status === 'met' ? 'border-l-green-500 bg-green-900/20/20' : 'border-l-slate-300'}`} data-testid={`benchmark-${bm.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <button onClick={() => toggleBenchmark(bm.id, bm.status)} data-testid={`toggle-bm-${bm.id}`}>
                        {bm.status === 'met' ? <CheckCircle size={18} weight="fill" className="text-green-500" /> : <Target size={18} className="text-slate-400" />}
                      </button>
                      <h4 className={`text-sm font-semibold ${bm.status === 'met' ? 'text-green-400' : ''}`} style={{ color: bm.status !== 'met' ? '#F1F5F9' : undefined }}>{bm.title}</h4>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 capitalize">{bm.type}</span>
                      {bm.phase && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{bm.phase}</span>}
                    </div>
                    {bm.description && <p className="text-xs text-slate-400 ml-6">{bm.description}</p>}
                    <div className="flex items-center gap-4 mt-1 ml-6 text-[10px] text-slate-400">
                      {bm.target_value && <span>Target: <strong>{bm.target_value}</strong></span>}
                      {bm.target_date && <span className="flex items-center gap-0.5"><Clock size={10} /> {bm.target_date}</span>}
                      {bm.criteria && <span>Criteria: {bm.criteria}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {benchmarks.length === 0 && <div className="card-flat p-8 text-center text-slate-400 text-sm">No benchmarks defined. Add KPIs and milestones to measure success.</div>}
          </div>
        </div>
      )}

      {activeTab === 'folders' && (
        <div data-testid="folders-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-400">{folders.length} subfolders</p>
            <Button size="sm" className="btn-primary text-xs" onClick={() => setShowFolderForm(true)} data-testid="add-folder-btn">
              <Plus size={14} className="mr-1" /> New Folder
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {folders.map(f => (
              <div key={f.id} className="card-flat p-4 hover:border-indigo-400 cursor-pointer transition-colors text-center group" onClick={() => { setSelectedFolder(f.id); setActiveTab('tasks'); }} data-testid={`folder-${f.id}`}>
                <FolderSimple size={32} className="mx-auto mb-2 text-indigo-400 group-hover:text-indigo-400" />
                <p className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{f.name}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{tasks.filter(t => t.subfolder === f.id).length} tasks</p>
              </div>
            ))}
            {folders.length === 0 && <div className="col-span-full card-flat p-8 text-center text-slate-400 text-sm">No subfolders. Create folders to organize tasks by area.</div>}
          </div>
        </div>
      )}

      {activeTab === 'linked' && (
        <div data-testid="linked-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-400">{links.length} linked entities</p>
            <Button size="sm" className="btn-primary text-xs" onClick={() => setShowLinkForm(true)} data-testid="link-entity-btn">
              <Plus size={14} className="mr-1" /> Link Entity
            </Button>
          </div>
          <div className="space-y-2">
            {links.map(link => (
              <div key={link.id} className="card-flat p-3 flex items-center gap-3" data-testid={`link-${link.id}`}>
                <LinkIcon size={16} className="text-indigo-400" />
                <div className="flex-1">
                  <span className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{link.entity_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 ml-2 capitalize">{link.entity_type}</span>
                </div>
                <span className="text-[10px] text-slate-400">{link.linked_by}</span>
                <button onClick={async () => { await api.unlinkEntity(id, link.id); load(); }} className="text-slate-300 hover:text-red-500"><Trash size={14} /></button>
              </div>
            ))}
            {links.length === 0 && <div className="card-flat p-8 text-center text-slate-400 text-sm">No linked entities. Link experiments, protocols, plasmids, strains, or samples.</div>}
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <div data-testid="team-section">
          <div className="space-y-2">
            {(project.team || []).map((member, i) => (
              <div key={i} className="card-flat p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-900/40 text-indigo-300 flex items-center justify-center text-sm font-bold">{member.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: '#F1F5F9' }}>{member.name}</p>
                  <p className="text-[10px] text-slate-400 capitalize">{member.role}</p>
                </div>
              </div>
            ))}
            {(!project.team || project.team.length === 0) && <div className="card-flat p-8 text-center text-slate-400 text-sm">No team members assigned. Edit the project to add team members.</div>}
          </div>
        </div>
      )}

      {/* Task Form Modal */}
      {showTaskForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTaskForm(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="task-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">Add Task</h2>
              <button onClick={() => setShowTaskForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={addTask} className="space-y-3">
              <input className="input-field w-full" placeholder="Task title *" required value={taskForm.title} onChange={e => setTaskForm(p => ({...p, title: e.target.value}))} data-testid="task-title-input" />
              <textarea className="input-field w-full" rows={2} placeholder="Description" value={taskForm.description} onChange={e => setTaskForm(p => ({...p, description: e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <select className="input-field" value={taskForm.priority} onChange={e => setTaskForm(p => ({...p, priority: e.target.value}))}>
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                  <option value="critical">Critical</option>
                </select>
                <select className="input-field" value={taskForm.phase} onChange={e => setTaskForm(p => ({...p, phase: e.target.value}))}>
                  <option value="">No Phase</option>
                  {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <input className="input-field w-full" placeholder="Assign to (name)" value={taskForm.assigned_to_name} onChange={e => setTaskForm(p => ({...p, assigned_to_name: e.target.value}))} />
              <input type="date" className="input-field w-full" value={taskForm.due_date} onChange={e => setTaskForm(p => ({...p, due_date: e.target.value}))} />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-task-btn">Add Task</Button>
            </form>
          </div>
        </div>
      )}

      {/* Benchmark Form Modal */}
      {showBenchmarkForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBenchmarkForm(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="benchmark-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">Add Benchmark</h2>
              <button onClick={() => setShowBenchmarkForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={addBenchmark} className="space-y-3">
              <input className="input-field w-full" placeholder="Benchmark title *" required value={bmForm.title} onChange={e => setBmForm(p => ({...p, title: e.target.value}))} data-testid="benchmark-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <select className="input-field" value={bmForm.type} onChange={e => setBmForm(p => ({...p, type: e.target.value}))}>
                  <option value="milestone">Milestone Gate</option>
                  <option value="kpi">Quantitative KPI</option>
                </select>
                <select className="input-field" value={bmForm.phase} onChange={e => setBmForm(p => ({...p, phase: e.target.value}))}>
                  <option value="">No Phase</option>
                  {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <textarea className="input-field w-full" rows={2} placeholder="Description" value={bmForm.description} onChange={e => setBmForm(p => ({...p, description: e.target.value}))} />
              <input className="input-field w-full" placeholder="Target value (e.g., >90% efficiency)" value={bmForm.target_value} onChange={e => setBmForm(p => ({...p, target_value: e.target.value}))} />
              <input className="input-field w-full" placeholder="Pass/fail criteria" value={bmForm.criteria} onChange={e => setBmForm(p => ({...p, criteria: e.target.value}))} />
              <input type="date" className="input-field w-full" value={bmForm.target_date} onChange={e => setBmForm(p => ({...p, target_date: e.target.value}))} />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-benchmark-btn">Add Benchmark</Button>
            </form>
          </div>
        </div>
      )}

      {/* Link Entity Modal */}
      {showLinkForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkForm(false)}>
          <div className="bg-slate-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="link-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">Link Entity</h2>
              <button onClick={() => setShowLinkForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={linkEntity} className="space-y-3">
              <select className="input-field w-full" value={linkForm.entity_type} onChange={e => setLinkForm(p => ({...p, entity_type: e.target.value}))}>
                {ENTITY_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
              <input className="input-field w-full" placeholder="Entity name *" required value={linkForm.entity_name} onChange={e => setLinkForm(p => ({...p, entity_name: e.target.value}))} data-testid="link-entity-name-input" />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-link-btn">Link</Button>
            </form>
          </div>
        </div>
      )}

      {/* Folder Form Modal */}
      {/* File Attachments */}
      <div className="card-flat p-5 mt-6">
        <FileAttachments entityType="projects" entityId={id} />
      </div>

      {showFolderForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowFolderForm(false)}>
          <div className="bg-slate-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="folder-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">New Folder</h2>
              <button onClick={() => setShowFolderForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={addFolder} className="space-y-3">
              <input className="input-field w-full" placeholder="Folder name *" required value={folderName} onChange={e => setFolderName(e.target.value)} data-testid="folder-name-input" />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-folder-btn">Create Folder</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
